import React from "react";

/**
 * The class filter bar.
 *
 * The portal has two distinct idioms for a control in a `form-group row`, and
 * the difference is deliberate:
 *
 *   - A **form** names its fields with a `<label>` *above* the control, in a
 *     `col-lg-6` two-column grid. Edit Batch is the reference.
 *   - A **filter bar** puts the caption *below* the control, as
 *     `<small className="form-text text-muted"><b>Filter</b> by X</small>`,
 *     several narrow `col-lg-2` columns across one row, applied on change.
 *     The Classes list (`Batches/Classes/Components/BatchFilter.js`, from
 *     line 112) is the reference.
 *
 * This is a filter bar. It narrows a list that is already on screen, it has
 * nothing to submit, and it sits above the thing it filters — so it takes the
 * second idiom, down to the column width and the bold `Filter` prefix. An
 * earlier pass converted it to labels-above; that made it read as a form to
 * fill in rather than a list to narrow, and it is reverted here.
 *
 * `aria-label` rather than the `<label htmlFor>` a form would use: the caption
 * underneath is helper text, not a label, so each select would otherwise reach
 * a screen reader unnamed. It is invisible, so the idiom is unaffected.
 */
export function BadgeFilters({ filters, setFilters, options, disabled }) {
  const update = (key) => (event) => {
    const { value } = event.target;
    setFilters((previous) => ({ ...previous, [key]: value }));
  };

  return (
    <form
      className="form form-label-right zr-no-print"
      onSubmit={(event) => event.preventDefault()}
    >
      <div className="form-group row">
        <div className="col-lg-2">
          <select
            className="form-control"
            name="activeFilter"
            aria-label="Filter by Status"
            disabled={disabled}
            onChange={update("active")}
            value={filters.active}
          >
            <option value="">All</option>
            <option value="Active">Active</option>
            <option value="In-Active">In-Active</option>
          </select>
          <small className="form-text text-muted">
            <b>Filter</b> by Status
          </small>
        </div>

        <div className="col-lg-2">
          <select
            className="form-control"
            name="dayFilter"
            aria-label="Filter by Day"
            disabled={disabled}
            onChange={update("day")}
            value={filters.day}
          >
            <option value="">All</option>
            {options.days.map((day) => (
              <option key={day} value={day}>
                {day}
              </option>
            ))}
          </select>
          <small className="form-text text-muted">
            <b>Filter</b> by Day
          </small>
        </div>

        <div className="col-lg-2">
          <select
            className="form-control"
            name="programFilter"
            aria-label="Filter by Program"
            disabled={disabled}
            onChange={update("program")}
            value={filters.program}
          >
            <option value="">All</option>
            {options.programs.map((program) => (
              <option key={program} value={program}>
                {program}
              </option>
            ))}
          </select>
          <small className="form-text text-muted">
            <b>Filter</b> by Program
          </small>
        </div>

        <div className="col-lg-2">
          <select
            className="form-control"
            name="deliveryFilter"
            aria-label="Filter by Delivery Type"
            disabled={disabled}
            onChange={update("delivery")}
            value={filters.delivery}
          >
            <option value="">All</option>
            {options.deliveries.map((delivery) => (
              <option key={delivery} value={delivery}>
                {delivery}
              </option>
            ))}
          </select>
          <small className="form-text text-muted">
            <b>Filter</b> by Delivery Type
          </small>
        </div>
      </div>
    </form>
  );
}
